import React, { useRef, useState } from "react";
import { Box, Flex, Text, Icon } from "@chakra-ui/react";
import { HiOutlinePlus, HiOutlineChatBubbleLeftRight } from "react-icons/hi2";
import ClusterCard from "./ClusterCard";
import { c, glassSurface } from "../../classroomColors";
import { brand, onTint } from "../../standeTokens";
import { waitedMinutes } from "./clusterQuestions";

const VISIBLE = 3;

/** One cluster in the rail: how long it has waited, and how many asked. */
const ClusterPill = ({ cluster, now, isDark, isOpen, onOpen, onClose }) => {
  const b = brand(isDark);
  const tint = onTint(isDark);
  const minutes = waitedMinutes(cluster, now);
  const urgent = minutes >= 3 || cluster.count >= 3;

  return (
    <Flex
      as="button"
      type="button"
      direction="column"
      align="center"
      justify="center"
      flexShrink={0}
      boxSize="46px"
      borderRadius="full"
      position="relative"
      bg={isOpen ? c.text : urgent ? b.lightRed : c.surface}
      color={isOpen ? c.surface : urgent ? tint.lightRed : c.textSub}
      border="1px solid"
      borderColor={isOpen ? c.text : urgent ? "transparent" : c.borderSoft}
      transition="background 0.15s, color 0.15s"
      onMouseEnter={onOpen}
      onFocus={onOpen}
      onClick={isOpen ? onClose : onOpen}
      aria-label={`${cluster.count} students asked: ${cluster.headline}`}
      aria-expanded={isOpen}
    >
      <Text fontSize="12px" fontWeight="600" lineHeight="1" sx={{ fontVariantNumeric: "tabular-nums" }}>
        {minutes < 1 ? "now" : minutes}
      </Text>
      {minutes >= 1 && (
        <Text fontSize="9px" lineHeight="1.1" opacity={0.75}>
          min
        </Text>
      )}
      {cluster.count > 1 && (
        <Flex
          position="absolute"
          top="-3px"
          right="-3px"
          minW="17px"
          h="17px"
          px="4px"
          borderRadius="full"
          align="center"
          justify="center"
          bg={b.secondaryOrange}
          color="#ffffff"
          fontSize="10px"
          fontWeight="600"
        >
          {cluster.count}
        </Flex>
      )}
    </Flex>
  );
};

/**
 * The question monitor that floats over the canvas (wireframe 130:54).
 *
 * A column of pills, most-urgent first, each one a group of students asking
 * the same thing. Hovering opens the card beside it — the rail stays narrow
 * so it never covers the board a teacher is drawing on.
 */
const ClusterRail = ({ clusters, now, isDark, onRespond, onDismiss, ...rest }) => {
  const [openId, setOpenId] = useState(null);
  const closeTimer = useRef(null);

  // A small grace period, or the card closes while the pointer crosses the
  // gap between the pill and the card.
  const open = (id) => {
    clearTimeout(closeTimer.current);
    setOpenId(id);
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenId(null), 220);
  };

  const [expanded, setExpanded] = useState(false);
  const shown = expanded ? clusters : clusters.slice(0, VISIBLE);
  const hidden = clusters.length - shown.length;
  const openCluster = clusters.find((cl) => cl.id === openId);

  return (
    <Box position="absolute" zIndex={15} onMouseLeave={scheduleClose} {...rest}>
      <Text fontSize="11px" fontWeight="500" color={c.textSub} mb="7px" textAlign="center">
        Questions
      </Text>

      <Flex
        direction="column"
        align="center"
        gap="9px"
        p="8px"
        borderRadius="200px"
        {...glassSurface}
      >
        {clusters.length === 0 ? (
          <Flex boxSize="46px" align="center" justify="center" borderRadius="full" bg={c.surfaceSubtle}>
            <Icon as={HiOutlineChatBubbleLeftRight} boxSize="17px" color={c.textMuted} />
          </Flex>
        ) : (
          shown.map((cluster) => (
            <ClusterPill
              key={cluster.id}
              cluster={cluster}
              now={now}
              isDark={isDark}
              isOpen={openId === cluster.id}
              onOpen={() => open(cluster.id)}
              onClose={() => setOpenId(null)}
            />
          ))
        )}

        {(hidden > 0 || expanded) && (
          <Flex
            as="button"
            type="button"
            boxSize="46px"
            borderRadius="full"
            align="center"
            justify="center"
            bg={c.surfaceSubtle}
            color={c.textSub}
            _hover={{ bg: c.hover, color: c.text }}
            onClick={() => setExpanded((v) => !v)}
            aria-label={expanded ? "Show fewer question groups" : `Show ${hidden} more question groups`}
          >
            {expanded ? (
              <Text fontSize="11px" fontWeight="500">Less</Text>
            ) : (
              <>
                <Icon as={HiOutlinePlus} boxSize="13px" />
                <Text fontSize="11px" fontWeight="500" ml="1px">{hidden}</Text>
              </>
            )}
          </Flex>
        )}
      </Flex>

      {openCluster && (
        <Box
          position="absolute"
          left="calc(100% + 12px)"
          top="24px"
          onMouseEnter={() => open(openCluster.id)}
          sx={{ animation: "standeFlyIn 0.14s ease-out" }}
        >
          <ClusterCard
            cluster={openCluster}
            now={now}
            isDark={isDark}
            onRespond={async (cl, text) => {
              await onRespond(cl, text);
              setOpenId(null);
            }}
            onDismiss={
              onDismiss
                ? (cl) => {
                    onDismiss(cl);
                    setOpenId(null);
                  }
                : undefined
            }
          />
        </Box>
      )}
    </Box>
  );
};

export default ClusterRail;
